import { Image } from 'expo-image';
import { Link, type Href } from 'expo-router';
import {
  CheckCircle2Icon,
  LockIcon,
  MapPinIcon,
  UsersIcon,
  XCircleIcon,
} from 'lucide-react-native';
import { ActivityIndicator, Alert, Pressable, StyleSheet, View } from 'react-native';

import { EventQuickActions } from '@/components/events/event-quick-actions';
import { Button } from '@sixam/ui/button';
import { Icon } from '@sixam/ui/icon';
import { Text } from '@sixam/ui/text';
import type { EventAttendanceStatus } from '@/lib/event-attendance';
import { BRAND } from '@sixam/config/brand';
import { getCurrentTime } from '@/lib/dev-clock';
import { memberEventHeadlineBadge } from '@/lib/event-list';
import { formatEventWhen, type Event } from '@/lib/events';

type MemberEventFeedRowProps = Readonly<{
  clubHref?: Href;
  attendanceStatus?: EventAttendanceStatus;
  clubName?: string;
  clubSlug?: string;
  editHref?: Href;
  event: Event;
  eventImageUrl?: string | null;
  href?: Href;
  isReadOnly?: boolean;
  isUnregistering?: boolean;
  now?: Date;
  onUnregister?: () => Promise<void> | void;
  showAttendanceActions?: boolean;
  showStatus?: boolean;
  timeZone?: string;
}>;

function clubInitials(name: string) {
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) {
    return '6A';
  }
  return words
    .slice(0, 2)
    .map((word) => word[0])
    .join('')
    .toUpperCase();
}

function attendanceLabel(status: EventAttendanceStatus) {
  if (status === 'pending') {
    return 'Payment pending';
  }
  if (status === 'registered') {
    return "You're going";
  }
  if (status === 'cancelled') {
    return 'Attendance cancelled';
  }
  return 'Event';
}

function eventLabel(
  event: Event,
  attendanceStatus: EventAttendanceStatus,
  showStatus: boolean,
  isReadOnly: boolean
) {
  if (showStatus) {
    if (isReadOnly) {
      return 'Past · Read only';
    }
    if (event.status === 'draft') {
      return 'Draft';
    }
    if (event.status === 'cancelled') {
      return 'Cancelled';
    }
    return 'Published';
  }

  return attendanceLabel(attendanceStatus);
}

type MemberEventFeedRowContentProps = Omit<MemberEventFeedRowProps, 'href'>;

const EVENT_FALLBACK_IMAGES = [
  require('@/assets/events/fallback-motion-01.webp'),
  require('@/assets/events/fallback-motion-02.webp'),
  require('@/assets/events/fallback-motion-03.webp'),
  require('@/assets/events/fallback-motion-04.webp'),
  require('@/assets/events/fallback-motion-05.webp'),
] as const;

function fallbackImageForEvent(eventId: string) {
  let hash = 0;
  for (let index = 0; index < eventId.length; index += 1) {
    hash = (hash * 31 + eventId.charCodeAt(index)) | 0;
  }
  return EVENT_FALLBACK_IMAGES[(hash >>> 0) % EVENT_FALLBACK_IMAGES.length];
}

function EventFeedRowDetails({
  attendanceStatus = 'none',
  event,
  eventImageUrl,
  isReadOnly = false,
  now = getCurrentTime(),
  showStatus = false,
  timeZone,
}: MemberEventFeedRowContentProps) {
  const headlineBadge = memberEventHeadlineBadge(event, attendanceStatus, now);
  const cancelledAttendance = attendanceStatus === 'cancelled';
  const eventWhen = formatEventWhen(event.starts_at, event.ends_at, timeZone);
  const imageSource = eventImageUrl ? { uri: eventImageUrl } : fallbackImageForEvent(event.id);

  return (
    <View className="mt-3 gap-2">
      <View className="h-6 flex-row items-start gap-2">
        <Text
          className="min-w-0 flex-1 text-xl font-bold leading-6"
          ellipsizeMode="tail"
          numberOfLines={1}
        >
          {event.title}
        </Text>
        {headlineBadge ? (
          <View className="flex-row items-center gap-1 rounded-full bg-primary px-2 py-1">
            <Icon as={CheckCircle2Icon} className="size-3.5 text-white" />
            <Text className="font-sans text-[10px] font-bold uppercase tracking-widest text-white">
              {headlineBadge}
            </Text>
          </View>
        ) : null}
        {cancelledAttendance ? (
          <View className="flex-row items-center gap-1 rounded-full bg-destructive/15 px-2 py-1">
            <Icon as={XCircleIcon} className="size-3.5 text-destructive" />
            <Text className="font-sans text-[10px] font-bold uppercase tracking-widest text-destructive">
              Cancelled
            </Text>
          </View>
        ) : null}
        {showStatus && event.status === 'draft' ? (
          <View className="rounded-full bg-muted px-2 py-1">
            <Text className="font-sans text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
              Draft
            </Text>
          </View>
        ) : null}
        {isReadOnly ? (
          <View className="rounded-full bg-muted px-2 py-1">
            <Text className="font-sans text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
              Past
            </Text>
          </View>
        ) : null}
      </View>
      <View className="h-6">
        <Text className="leading-6 text-foreground" ellipsizeMode="tail" numberOfLines={1}>
          {event.description}
        </Text>
      </View>
      <View
        className="h-44 justify-between overflow-hidden rounded-md border border-white/10 p-4"
        style={{ backgroundColor: BRAND.color.graphite }}
      >
        <Image
          cachePolicy="memory-disk"
          contentFit="cover"
          source={imageSource}
          style={StyleSheet.absoluteFill}
        />
        {!eventImageUrl ? (
          <View
            className="absolute inset-0"
            pointerEvents="none"
            style={{
              backgroundColor: BRAND.color.orange,
              mixBlendMode: 'multiply',
              opacity: 0.18,
            }}
          />
        ) : null}
        <View className="absolute inset-0 bg-black/35" pointerEvents="none" />
        <Text variant="label" className="text-white/80">
          {eventLabel(event, attendanceStatus, showStatus, isReadOnly)}
        </Text>
        <View>
          <Text className="font-display text-3xl font-bold uppercase text-white">
            {event.place_name}
          </Text>
          <Text className="font-mono text-sm text-white/80">{eventWhen}</Text>
        </View>
      </View>
    </View>
  );
}

function EventFeedRowAttendance({ event }: Pick<MemberEventFeedRowProps, 'event'>) {
  return (
    <View className="mt-3 flex-row items-center">
      <View
        accessibilityLabel={`${event.attendee_count} people going`}
        className="h-10 items-center justify-center rounded-full border border-border bg-card px-4"
      >
        <Text className="font-mono font-bold">{event.attendee_count}</Text>
      </View>
    </View>
  );
}

function EventFeedRowLocation({ event }: Pick<MemberEventFeedRowProps, 'event'>) {
  return (
    <View className="mt-2 flex-row items-center gap-2">
      <Icon as={MapPinIcon} className="size-4 text-muted-foreground" />
      <Text variant="muted" className="min-w-0 flex-1" numberOfLines={1}>
        {event.place_address}
      </Text>
      <Icon as={UsersIcon} className="size-4 text-muted-foreground" />
    </View>
  );
}

function ClubIdentity({
  clubHref,
  clubName,
  clubSlug,
  event,
  timeZone,
}: Pick<MemberEventFeedRowProps, 'clubHref' | 'clubName' | 'clubSlug' | 'event' | 'timeZone'>) {
  const displayClubName = clubName ?? '6am BKK';
  const displayHandle = clubSlug ? clubSlug.replaceAll('-', '.') : displayClubName.toLowerCase();
  const identity = (
    <>
      <View className="size-8 items-center justify-center rounded-full bg-primary">
        <Text className="font-sans text-xs font-bold text-white">
          {clubInitials(displayClubName)}
        </Text>
      </View>
      <Text className="min-w-0 flex-1 font-bold" numberOfLines={1}>
        {displayHandle}{' '}
        <Text className="font-normal text-muted-foreground">
          {formatEventWhen(event.starts_at, event.ends_at, timeZone).split(' · ')[0]}
        </Text>
      </Text>
    </>
  );

  if (!clubHref) {
    return <View className="min-w-0 flex-1 flex-row items-center gap-2">{identity}</View>;
  }

  return (
    <Link href={clubHref} asChild>
      <Pressable
        accessibilityLabel={`Open ${displayClubName} profile`}
        className="min-h-11 min-w-0 flex-1 flex-row items-center gap-2 active:opacity-80"
      >
        {identity}
      </Pressable>
    </Link>
  );
}

function confirmUnregister(eventTitle: string): Promise<boolean> {
  if (process.env.EXPO_OS === 'web') {
    return Promise.resolve(
      globalThis.confirm?.(`Unregister from ${eventTitle}? You will give up your place.`) ?? false
    );
  }

  return new Promise((resolve) => {
    Alert.alert(
      'Unregister from event?',
      `You will give up your place at ${eventTitle}.`,
      [
        { onPress: () => resolve(false), style: 'cancel', text: 'Keep registration' },
        { onPress: () => resolve(true), style: 'destructive', text: 'Unregister' },
      ],
      { cancelable: true, onDismiss: () => resolve(false) }
    );
  });
}

function EventFeedRowActions({
  event,
  href,
  isUnregistering = false,
  onUnregister,
}: Pick<MemberEventFeedRowProps, 'event' | 'href' | 'isUnregistering' | 'onUnregister'>) {
  async function unregister() {
    if (!onUnregister || !(await confirmUnregister(event.title))) return;

    try {
      await onUnregister();
    } catch (error) {
      Alert.alert(
        'Could not unregister',
        error instanceof Error ? error.message : 'Please try again.'
      );
    }
  }

  return (
    <View className="mt-4 flex-row gap-2">
      {href ? (
        <Link href={href} asChild>
          <Button className="flex-1" variant="secondary">
            <Text>View details</Text>
          </Button>
        </Link>
      ) : null}
      {onUnregister ? (
        <Button
          className="flex-1"
          disabled={isUnregistering}
          onPress={() => void unregister()}
          variant="destructive"
        >
          {isUnregistering ? <ActivityIndicator color={BRAND.color.white} /> : null}
          <Text>{isUnregistering ? 'Updating…' : 'Unregister'}</Text>
        </Button>
      ) : null}
    </View>
  );
}

export function MemberEventFeedRow({ clubHref, href, ...rowProps }: MemberEventFeedRowProps) {
  const details = <EventFeedRowDetails clubHref={clubHref} {...rowProps} />;

  return (
    <View className="border-b border-border bg-background px-4 py-4">
      <View className="flex-row items-start justify-between gap-3">
        <ClubIdentity clubHref={clubHref} {...rowProps} />
        <View className="flex-row items-center">
          {rowProps.isReadOnly ? (
            <View
              accessibilityLabel="Past event, read only"
              className="size-11 items-center justify-center"
            >
              <Icon as={LockIcon} className="size-4 text-muted-foreground" />
            </View>
          ) : null}
          <EventQuickActions
            clubName={rowProps.clubName}
            clubSlug={rowProps.clubSlug}
            editHref={rowProps.editHref}
            event={rowProps.event}
            timeZone={rowProps.timeZone}
          />
        </View>
      </View>

      {href ? (
        <Link href={href} asChild>
          <Pressable className="active:opacity-80">{details}</Pressable>
        </Link>
      ) : (
        details
      )}
      {rowProps.showAttendanceActions ? <EventFeedRowActions href={href} {...rowProps} /> : null}
      <EventFeedRowAttendance event={rowProps.event} />
      <EventFeedRowLocation event={rowProps.event} />
    </View>
  );
}
